import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { toast } from 'sonner'
import axios from 'axios'
import {
  User,
  Mail,
  Shield,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  Phone,
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { authApi, type ChangePasswordErrorCode } from '@/api/auth'
import type { ApiResponse } from '@/api/client'
import { useChangePassword } from '@/hooks/learner/useChangePassword'
import { NameEditModal } from '@/components/admin/profile/NameEditModal'
import { PhoneVerifyModal } from '@/components/admin/profile/PhoneVerifyModal'

interface InfoItemProps {
  icon: React.ReactNode
  label: string
  value: string
  badge?: string
  action?: React.ReactNode
}

function InfoItem({ icon, label, value, badge, action }: InfoItemProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-gray-400 mt-1">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm text-gray-600">{label}</p>
          {badge && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-medium">
              {badge}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="font-medium text-gray-900 truncate">{value || '-'}</p>
          {action}
        </div>
      </div>
    </div>
  )
}

// Role key → i18n key mapping
const ROLE_KEYS: Record<string, string> = {
  owner: 'admin:profile.roles.owner',
  admin: 'admin:profile.roles.admin',
  admin_learner: 'admin:profile.roles.adminLearner',
  member: 'admin:profile.roles.member',
}

// Server error code → i18n key mapping
const SERVER_ERROR_KEYS: Record<ChangePasswordErrorCode, string> = {
  INVALID_CURRENT_PASSWORD: 'admin:profile.serverErrors.INVALID_CURRENT_PASSWORD',
  WEAK_PASSWORD: 'admin:profile.serverErrors.WEAK_PASSWORD',
  SAME_AS_CURRENT_PASSWORD: 'admin:profile.serverErrors.SAME_AS_CURRENT_PASSWORD',
}

interface PasswordChangeModalProps {
  isOpen: boolean
  onClose: () => void
  onPasswordChanged: () => void
}

function PasswordChangeModal({ isOpen, onClose, onPasswordChanged }: PasswordChangeModalProps) {
  const { t } = useTranslation(['admin', 'common'])
  const mutation = useChangePassword()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [errors, setErrors] = useState<{ current?: string; new?: string; confirm?: string; general?: string }>({})

  const hasMinLength = newPassword.length >= 8
  const hasLetter = /[a-zA-Z]/.test(newPassword)
  const hasNumber = /[0-9]/.test(newPassword)
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0

  const resetForm = useCallback(() => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setShowCurrent(false)
    setShowNew(false)
    setShowConfirm(false)
    setErrors({})
  }, [])

  useEffect(() => {
    if (isOpen) resetForm()
  }, [isOpen, resetForm])

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const validate = (): boolean => {
    const next: typeof errors = {}
    if (!currentPassword) next.current = t('admin:profile.validation.currentRequired')
    if (!newPassword) next.new = t('admin:profile.validation.newRequired')
    else if (!hasMinLength) next.new = t('admin:profile.validation.newMinLength')
    else if (!hasLetter || !hasNumber) next.new = t('admin:profile.validation.newLetterNumber')
    if (!confirmPassword) next.confirm = t('admin:profile.validation.confirmRequired')
    else if (newPassword !== confirmPassword) next.confirm = t('admin:profile.validation.confirmMatch')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSave = () => {
    if (!validate()) return
    setErrors({})
    mutation.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          toast.success(t('admin:profile.toast.passwordChanged'))
          onPasswordChanged()
          handleClose()
        },
        onError: (err) => {
          let code: ChangePasswordErrorCode | null = null
          if (axios.isAxiosError(err)) {
            const data = err.response?.data as ApiResponse<unknown> | undefined
            const raw = (data?.error as unknown) as string | undefined
            if (raw && raw in SERVER_ERROR_KEYS) {
              code = raw as ChangePasswordErrorCode
            }
          }
          if (code === 'INVALID_CURRENT_PASSWORD') {
            setErrors({ current: t(SERVER_ERROR_KEYS[code] as Parameters<typeof t>[0]) })
          } else if (code === 'SAME_AS_CURRENT_PASSWORD' || code === 'WEAK_PASSWORD') {
            setErrors({ new: t(SERVER_ERROR_KEYS[code] as Parameters<typeof t>[0]) })
          } else {
            setErrors({ general: t('admin:profile.modal.generalError') })
          }
        },
      },
    )
  }

  const isLoading = mutation.isPending

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div>
              <div>{t('admin:profile.modal.title')}</div>
              <p className="text-sm text-gray-500 mt-1 font-normal">{t('admin:profile.modal.subtitle')}</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="lc-current-password" className="block text-sm font-medium text-gray-700">
              {t('admin:profile.modal.currentPasswordLabel')}
            </label>
            <div className="relative">
              <input
                id="lc-current-password"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t('admin:profile.modal.currentPasswordPlaceholder')}
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors ${
                  errors.current ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowCurrent(!showCurrent)}
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.current && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.current}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="lc-new-password" className="block text-sm font-medium text-gray-700">
              {t('admin:profile.modal.newPasswordLabel')}
            </label>
            <div className="relative">
              <input
                id="lc-new-password"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('admin:profile.modal.newPasswordPlaceholder')}
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors ${
                  errors.new ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.new && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.new}
              </p>
            )}
            {newPassword.length > 0 && (
              <div className="mt-3 space-y-1 text-sm">
                <div className={hasMinLength ? 'text-green-600' : 'text-gray-400'}>
                  {hasMinLength ? '✓' : '✗'} {t('admin:profile.modal.requirements.minLength')}
                </div>
                <div className={hasLetter && hasNumber ? 'text-green-600' : 'text-gray-400'}>
                  {hasLetter && hasNumber ? '✓' : '✗'} {t('admin:profile.modal.requirements.lettersAndNumbers')}
                </div>
                <div className={hasSpecial ? 'text-green-600' : 'text-gray-400'}>
                  {hasSpecial ? '✓' : '✗'} {t('admin:profile.modal.requirements.specialChar')}
                </div>
                {confirmPassword.length > 0 && (
                  <div className={passwordsMatch ? 'text-green-600' : 'text-red-500'}>
                    {passwordsMatch ? '✓' : '✗'} {t('admin:profile.modal.requirements.passwordMatch')}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="lc-confirm-password" className="block text-sm font-medium text-gray-700">
              {t('admin:profile.modal.confirmPasswordLabel')}
            </label>
            <div className="relative">
              <input
                id="lc-confirm-password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('admin:profile.modal.confirmPasswordPlaceholder')}
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors ${
                  errors.confirm ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirm && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.confirm}
              </p>
            )}
          </div>

          {errors.general && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {errors.general}
            </p>
          )}
        </div>
        <DialogFooter>
          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common:button.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('admin:profile.modal.changeButton')}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function AdminProfile() {
  const { t, i18n } = useTranslation(['admin', 'common'])
  const { user, applyUserUpdate } = useAuthStore()
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [isNameModalOpen, setIsNameModalOpen] = useState(false)
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false)

  const { data: meData, refetch: refetchMe } = useQuery({
    queryKey: ['admin-profile-me'],
    queryFn: async () => {
      const res = await authApi.me()
      return res.data.data as {
        passwordChangedAt?: string | null
        phoneNumber?: string | null
        countryCode?: string | null
        isPhoneVerified?: boolean
      }
    },
    staleTime: 30_000,
  })

  const initials = user?.fullName?.slice(0, 2) || user?.email?.slice(0, 2) || '??'
  const roleLabel = user?.role
    ? t((ROLE_KEYS[user.role] ?? user.role) as Parameters<typeof t>[0], { defaultValue: user.role })
    : '-'

  const handlePasswordChanged = () => {
    refetchMe()
  }

  const handleNameSaved = (newName: string) => {
    applyUserUpdate({ fullName: newName })
    refetchMe()
  }

  const handlePhoneUpdated = () => {
    refetchMe()
  }

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return t('admin:profile.password.never')
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return t('admin:profile.password.never')
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <User className="w-8 h-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">{t('admin:profile.title')}</h1>
        </div>
        <p className="text-gray-500">{t('admin:profile.subtitle')}</p>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('admin:profile.basicInfoTitle')}</h2>
          </div>

          <div className="flex items-start gap-6">
            <div className="h-24 w-24 rounded-full bg-primary-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {initials}
            </div>

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoItem
                icon={<User className="h-4 w-4" />}
                label={t('admin:profile.fields.name')}
                value={user?.fullName || ''}
                action={
                  <button
                    onClick={() => setIsNameModalOpen(true)}
                    className="text-sm font-medium text-primary-600 hover:underline shrink-0"
                  >
                    {t('admin:profile.name.editButton')}
                  </button>
                }
              />
              <InfoItem
                icon={<Mail className="h-4 w-4" />}
                label={t('admin:profile.fields.email')}
                value={user?.email || ''}
                badge={t('admin:profile.badges.readonly')}
              />
              <InfoItem
                icon={<Shield className="h-4 w-4" />}
                label={t('admin:profile.fields.role')}
                value={roleLabel}
                badge={t('admin:profile.badges.readonly')}
              />
              <InfoItem
                icon={<Phone className="h-4 w-4" />}
                label={t('admin:profile.phone.fieldLabel')}
                value={meData?.phoneNumber || t('admin:profile.phone.notRegistered')}
                badge={meData?.isPhoneVerified ? t('admin:profile.phone.verifiedBadge') : undefined}
                action={
                  <button
                    onClick={() => setIsPhoneModalOpen(true)}
                    className="text-sm font-medium text-primary-600 hover:underline shrink-0"
                  >
                    {meData?.phoneNumber
                      ? t('admin:profile.phone.changeButton')
                      : t('admin:profile.phone.registerButton')}
                  </button>
                }
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('admin:profile.securityTitle')}</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 mb-1">{t('admin:profile.fields.password')}</p>
                <p className="text-sm text-gray-600">
                  {t('admin:profile.password.lastChanged', { date: formatDate(meData?.passwordChangedAt) })}
                </p>
              </div>
              <button
                onClick={() => setIsPasswordModalOpen(true)}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Lock className="h-4 w-4" />
                {t('admin:profile.password.changeButton')}
              </button>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                {t('admin:profile.password.advice')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <PasswordChangeModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onPasswordChanged={handlePasswordChanged}
      />

      <NameEditModal
        isOpen={isNameModalOpen}
        currentName={user?.fullName || ''}
        onClose={() => setIsNameModalOpen(false)}
        onSaved={handleNameSaved}
      />

      <PhoneVerifyModal
        isOpen={isPhoneModalOpen}
        mode={meData?.phoneNumber ? 'change' : 'register'}
        onClose={() => setIsPhoneModalOpen(false)}
        onUpdated={handlePhoneUpdated}
      />
    </div>
  )
}
